/**
 * 🎥 **화상 무대가 카메라·마이크를 쓸 수 있는가** — `Permissions-Policy` 판정.
 *
 * ## 🔴 왜 이게 판정거리인가 (2026-09-02 운영자 신고)
 * 행사 「북토크」에 **호스트로 들어갔는데 카메라와 마이크가 안 켜졌다.** 다른 참여자는
 * 멀쩡했다 — 그들은 우리 페이지를 안 거치고 `meet.jit.si` 로 바로 들어왔기 때문이다.
 * 그래서 「내 폰만 이상한가」로 보였고, 원인은 화면 어디에도 안 적혀 있었다.
 *
 * 원인은 배포 헤더 한 줄이었다:
 *
 *     Permissions-Policy: camera=(self), geolocation=(self), microphone=(), payment=()
 *
 * `microphone=()` 는 **통째로 끔**이고 `camera=(self)` 는 **우리 출처만**이다.
 * 화상은 `meet.jit.si` iframe 인데, iframe 의 `allow="camera; microphone"` 은
 * **상위 문서가 가진 권한을 넘겨주는 것**이라 상위에서 막혔으면 아무 소용이 없다.
 * 같은 헤더가 **방과후 화상 수업**(`ClassStage`)도 막고 있었다 — 학교가 쓰는 기능이다.
 *
 * ## 재 볼 때 조심할 것
 * `document.featurePolicy.allowsFeature("microphone", "https://whaleon.us")` 처럼
 * **실제로 끼우는 출처**를 넣어 잰다.
 * 고친 직후에 `false` 가 나오면 **CDN 전파**를 먼저 의심할 것 — 서비스워커가 아니다
 * (첫 화면 이동은 네트워크 우선이라 워커는 새 헤더를 안 붙든다. 처음에 워커 탓으로
 * 잘못 짚었다가 다시 재서 바로잡았다).
 *
 * ## 왜 shared 에 두나
 * 그 한 줄은 코드가 아니라 **배포 설정**(`apps/web/public/_headers`)이라, 지금까지
 * 아무 검사도 안 지켰다. 여기에 판정을 두고 검사가 파일을 읽어 물어본다 —
 * 「그 iframe 이 카메라를 쓸 수 있나」를 사람 눈이 아니라 코드가 답하게.
 */

/**
 * 🗑 **`STAGE_ORIGIN` 을 걷어냈다**(2026-09-03).
 *
 * 「화상 무대를 띄우는 곳」이라고 적혀 있었지만 `meet.jit.si` 는 이제 그 자리가 아니다 —
 * 무대는 운영자가 고른 서비스로 열리고, 그 목록과 헤더는 `MEET_SERVICES` /
 * `apps/web/public/_headers` 가 짝을 이룬다(`headersFile.test.ts` 가 견준다).
 * 한 곳에 낡은 답이 남아 있으면 다음 사람이 그걸 근거로 판단한다.
 *
 * 같이 걷어낸 `stageMediaBlocked` 도 그 상수를 기본값으로 쓰고 있었고,
 * 부르는 곳이 제 검사뿐이었다. 판정은 아래 `policyAllows` 로 충분하다.
 */

export interface PolicyVerdict {
  /** 이 기능을 이 출처가 쓸 수 있나. */
  ok: boolean;
  /** 왜 안 되는지 — 사람이 읽을 한 줄. */
  say?: string;
}

/**
 * `Permissions-Policy` 헤더 한 줄에서 한 기능의 허용 목록을 뽑는다.
 * 없으면 `null` — **「비었다」와 「안 적혀 있다」는 다르다**(안 적혀 있으면 기본 허용이다).
 */
export function policyAllowlist(header: string, feature: string): string[] | null {
  // `camera=(self "https://x")` · `camera=self` · `camera=*` 모두 받는다
  const re = new RegExp(`(?:^|[,;\\s])${feature}\\s*=\\s*(\\([^)]*\\)|[^,;\\s]+)`, "i");
  const m = re.exec(header);
  if (!m) return null;
  const raw = m[1]!.trim();
  const inner = raw.startsWith("(") ? raw.slice(1, -1) : raw;
  return inner
    .split(/\s+/)
    .map((t) => t.replace(/^["']|["']$/g, ""))
    .filter(Boolean);
}

/**
 * 이 헤더에서 `origin` 이 `feature` 를 쓸 수 있나.
 *
 * 🔴 `self` 는 **우리 출처**만이다 — 다른 출처의 iframe 은 이름으로 적혀 있어야 한다.
 * 「iframe 에 `allow=` 를 달았으니 되겠지」가 이번 사고의 정확한 오해였다.
 */
export function policyAllows(header: string, feature: string, origin: string): PolicyVerdict {
  const list = policyAllowlist(header, feature);
  if (list === null) return { ok: true }; // 안 적혀 있으면 기본 허용
  if (list.length === 0) {
    return { ok: false, say: `\`${feature}=()\` — 통째로 꺼져 있어요(우리 화면에서도 못 씁니다).` };
  }
  if (list.includes("*")) return { ok: true };
  if (list.includes(origin)) return { ok: true };
  return {
    ok: false,
    say:
      `\`${feature}\` 가 ${list.join(" ")} 에게만 열려 있어요. ` +
      `${origin} 는 **다른 출처**라, iframe 의 \`allow="${feature}"\` 로도 못 켭니다.`,
  };
}

/**
 * 🎥 **이 무대를 어느 주소로 여나** — 운영자가 넣은 주소, **그것뿐**이다.
 *
 * ## 🔴 Jitsi 로 되돌아가지 않는다 (2026-09-03 결정을 코드가 안 지키고 있었다)
 * 화상은 **운영자가 고른다**(웨일온·구글밋·줌·Jitsi — `MEET_KINDS`).
 * 🔴 한때 여기 「웨일온으로 가기로 정했다」라고 적혀 있었다. 하루 만에 거짓이 됐다 —
 * 하나로 정해 두면 그게 안 되는 사람에게는 길이 없어서 고르게 바꿨다(2026-09-03).
 * 어느 서비스냐는 **코드에 안 박는다** — 그게 이 설계의 요점이다.
 * 어느 쪽이든 참여자는 계정 없이 링크로 들어온다(불변식 1 과 맞다).
 *
 * 그런데 코드는 회의 주소가 비면 **조용히 옛 Jitsi 방 이름으로 되돌아갔다.** 그게
 * 「그래도 뭐라도 열린다」로 보였지만 사실이 아니다 — `meet.jit.si` 를 우리가 붙여 쓰는 건
 * 8x8 이 데모로만 허용해서 **통화가 5분 뒤 끊긴다.** 운영자는 행사 중에 그 안내를 받았고,
 * 그 뒤로도 「북토크가 여전히 jitsi 로 열려」를 겪었다(운영 게시본에 회의 주소가 비어 있었다).
 *
 * **되는 척하다 사람들이 모인 뒤에 끊기는 것보다, 안 열린다고 미리 말하는 편이 낫다.**
 * 그래서 폴백을 없앤다. 주소가 없으면 `null` 이고, 화면은 아직 준비 안 됐다고 말한다.
 *
 * 어느 서비스냐는 여전히 코드에 안 박는다 — 줌·구글밋 링크를 넣어도 그대로 열린다.
 */
export function stageMeetUrl(
  stage: { meetUrl?: string } | undefined | null,
): string | null {
  return stage?.meetUrl?.trim() || null;
}

/**
 * 🎥 **화상이 아직 안 열리는 행사인가** — 옛 Jitsi 방 이름만 남아 있는 경우.
 *
 * 회의 주소가 없으면 아직 안 열린다(폴백 없음). 어느 서비스를 쓰는지는 운영자가 고른다.
 * 그런데 옛 방 이름이 남아 있으면 운영자는 **채웠다고 착각한다** — 행사 당일에
 * 사람들이 모인 뒤에야 알게 된다. 그 자리를 여기서 잡는다.
 *
 * 「방 이름이 위험한가」(`eventRoomRisky`)와 **다른 문제**다 — 이건 아예 안 열린다는 뜻이다.
 */
export function stageJitsiOnly(
  stage: { meetUrl?: string; jitsi?: string } | undefined | null,
  /**
   * 🏠 **기본 회의 주소** — 운영자가 방 하나를 만들어 두면 행사가 그걸 쓴다.
   *
   * 🔴 이걸 안 보면 **고칠 수 없는 빨간 줄**이 된다: 운영자가 기본 방을 넣어도 관리자는
   * 계속 「화상이 아직 안 열려요」라고 말한다. 아이 화면(`/api/events`)은 이미 기본 방으로
   * 열리는데 관리자만 딴소리를 하는 것이다 — 그러면 목록 전체를 안 믿게 된다.
   * 이 저장소가 「안 쓰는 값으로 잔소리」로 이미 한 번 당한 자리다.
   */
  siteDefault?: string | null,
): boolean {
  if (stage?.meetUrl?.trim()) return false;
  if (siteDefault?.trim()) return false; // 기본 방으로 열린다
  return !!stage?.jitsi?.trim();
}

/**
 * 운영자에게 할 말 한 줄 — 무엇이 일어나는지부터.
 *
 * 🔴 **문구가 한 번 거짓말이 됐다**(2026-09-03). 전엔 「옛 Jitsi 방 이름만 남아 있어요
 * (이젠 안 씁니다)」였는데, 그날 Jitsi 를 **다시 고를 수 있게** 만들었다. 안 쓰는 게 아니라
 * **쓰는 방법이 달라진 것**이다 — 옛 방식은 방 이름을 받아 화면에 **끼워** 넣었고(그래서
 * 5분 뒤 끊겼다), 지금은 주소를 받아 **새 창으로** 연다.
 *
 * 그러니 「안 씁니다」라고 하면 운영자는 멀쩡한 방을 버리고 새로 만든다. 할 일은 하나다:
 * 그 방 주소를 「회의 주소」 칸으로 옮기는 것.
 */
export const JITSI_ONLY_SAY =
  "화상이 아직 안 열려요 — 옛 방식(방 이름만)으로 남아 있어요. 그건 화면에 끼워 넣는 " +
  "방식이라 5분 뒤 끊깁니다. 위 「회의 주소」 칸에 방 주소를 넣어 주세요 — " +
  "웨일온·구글밋·줌은 물론 같은 Jitsi 방도 주소로 넣으면 새 창으로 열려서 안 끊겨요.";
